// ============================================================
// FICHIER : boutique/modules/upload.js
// RÔLE : Étape 1 — Photo : zone de dépôt (glisser-déposer + clic),
//        validation client (type / taille), aperçu local, puis upload
//        via POST /image/upload (FormData).
//        ⚠️ L'envoi d'une photo peut prendre plusieurs secondes : une
//        ANIMATION D'ATTENTE (anneau + points + jauge balayée) est
//        affichée DÈS QUE le fichier est accepté (donc aussi pendant la
//        lecture locale qui précède l'envoi réseau), avec une durée
//        minimale d'affichage pour ne jamais « clignoter ». La zone de
//        dépôt est verrouillée (impossible de relancer un envoi en double).
//        ⚠️ RÉPONSE ILLISIBLE : le serveur enregistre l'image AVANT de
//        répondre ; un corps de réponse corrompu (avertissement PHP
//        affiché, réponse perdue…) aboutit à « Réponse invalide du serveur. »
//        alors que l'upload a réussi. On vérifie donc l'historique avant
//        d'annoncer un échec : si l'image est bien là, on continue
//        silencieusement (aucun faux message d'erreur).
//        Les notifications d'erreur sont gérées ICI (appel silencieux), pour
//        ne jamais en afficher une alors que l'envoi a réussi.
// ============================================================

window.Toilesia = window.Toilesia || {};
(function () {
    'use strict';

    const MAX_OCTETS = 10 * 1024 * 1024; // 10 Mo (cohérent avec le backend)
    const TYPES_AUTORISES = ['image/jpeg', 'image/png', 'image/webp'];
    // Durée minimale d'affichage de l'animation d'envoi : sans elle, un envoi
    // rapide (ou un échec immédiat) ferait disparaître l'attente avant que le
    // client ne l'ait vue — d'où l'impression qu'« il manque un chargement ».
    const DUREE_MIN_ATTENTE = 600;

    let _imageId = null;
    let _urlApercu = null;
    let _ui = null;            // éléments de l'écran courant (voir afficher)
    let _envoiEnCours = false; // verrou pendant l'envoi réseau
    let _debutEnvoi = 0;       // horodatage du début de l'attente (durée mini)

    /**
     * Construit l'écran « Photo ».
     * @param {HTMLElement} conteneur Section d'affichage
     * @param {function} onUploaded Callback (imageId, urlApercu) après un upload réussi
     */
    function afficher(conteneur, onUploaded) {
        conteneur.innerHTML = '';

        const zone = document.createElement('div');
        zone.className = 'tls-upload';

        // ---- Zone de dépôt ----
        const drop = document.createElement('div');
        drop.className = 'tls-drop';
        drop.setAttribute('role', 'button');
        drop.setAttribute('tabindex', '0');
        drop.setAttribute('aria-label', 'Déposer ou choisir une photo');
        drop.innerHTML =
            '<div class="tls-drop-icone">📷</div>' +
            '<p class="tls-drop-texte">Déposez votre photo ici<br>ou</p>' +
            '<button type="button" class="tls-btn tls-btn-principal" data-role="choisir">Choisir une photo</button>' +
            '<p class="tls-drop-info">JPG, PNG ou WebP · 10 Mo maximum</p>';

        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/jpeg,image/png,image/webp';
        input.style.display = 'none';

        // ---- Aperçu local ----
        const apercuZone = document.createElement('div');
        apercuZone.className = 'tls-upload-apercu';
        apercuZone.style.display = 'none';

        // ---- Erreur de validation ----
        const erreurZone = document.createElement('div');
        erreurZone.className = 'tls-upload-erreur';
        erreurZone.style.display = 'none';

        // ---- Attente d'envoi (animée, masquée au départ) ----
        const envoiZone = document.createElement('div');
        envoiZone.className = 'tls-upload-envoi';
        envoiZone.style.display = 'none';
        envoiZone.setAttribute('role', 'status');
        envoiZone.setAttribute('aria-live', 'polite');
        envoiZone.innerHTML =
            '<div class="tls-upload-envoi-ligne">' +
            '  <span class="tls-spinner" aria-hidden="true"></span>' +
            '  <span class="tls-upload-envoi-texte">Envoi de votre photo</span>' +
            '  <span class="tls-upload-envoi-pts" aria-hidden="true"><i></i><i></i><i></i></span>' +
            '</div>' +
            '<div class="tls-upload-jauge" aria-hidden="true">' +
            '  <span class="tls-upload-jauge-remplissage"></span>' +
            '</div>';

        zone.appendChild(drop);
        zone.appendChild(input);
        zone.appendChild(apercuZone);
        zone.appendChild(envoiZone);
        zone.appendChild(erreurZone);
        conteneur.appendChild(zone);

        // Références de l'écran courant (utilisées par les helpers)
        _ui = {
            drop: drop,
            input: input,
            apercuZone: apercuZone,
            erreurZone: erreurZone,
            envoiZone: envoiZone,
            texteEnvoi: envoiZone.querySelector('.tls-upload-envoi-texte')
        };
        _envoiEnCours = false;
        _debutEnvoi = 0;
        // ---- Événements ----
        drop.querySelector('[data-role="choisir"]').addEventListener('click', (e) => {
            e.stopPropagation();
            if (_envoiEnCours) return;
            input.click();
        });
        drop.addEventListener('click', () => {
            if (_envoiEnCours) return;
            input.click();
        });
        drop.addEventListener('keydown', (e) => {
            if (_envoiEnCours) return;
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                input.click();
            }
        });

        input.addEventListener('change', () => {
            if (input.files && input.files[0]) {
                traiterFichier(input.files[0], onUploaded);
            }
            input.value = ''; // permet de re-sélectionner le même fichier
        });

        // Glisser-déposer
        ['dragenter', 'dragover'].forEach((ev) => {
            drop.addEventListener(ev, (e) => {
                e.preventDefault();
                if (_envoiEnCours) return;
                drop.classList.add('tls-drop-survol');
            });
        });
        ['dragleave', 'drop'].forEach((ev) => {
            drop.addEventListener(ev, (e) => {
                e.preventDefault();
                drop.classList.remove('tls-drop-survol');
            });
        });
        drop.addEventListener('drop', (e) => {
            if (_envoiEnCours) return;
            const fichiers = e.dataTransfer && e.dataTransfer.files;
            if (fichiers && fichiers[0]) {
                traiterFichier(fichiers[0], onUploaded);
            }
        });
    }

    // ------------------------------------------------------------
    // ATTENTE D'ENVOI (le temps que la photo monte au serveur)
    // ------------------------------------------------------------

    /**
     * Verrouille (ou libère) l'écran pendant l'envoi : la zone de dépôt
     * n'accepte plus de fichier et l'aperçu est légèrement estompé.
     */
    function verrouiller(actif) {
        if (!_ui) return;
        _envoiEnCours = actif;
        _ui.drop.classList.toggle('tls-drop-occupe', actif);
        _ui.drop.setAttribute('aria-busy', actif ? 'true' : 'false');
        _ui.input.disabled = actif;
        _ui.apercuZone.classList.toggle('tls-envoi-en-cours', actif);
    }

    /** Affiche l'attente animée (anneau + points + jauge balayée). */
    function afficherAttente() {
        if (!_ui) return;
        _ui.envoiZone.classList.remove('tls-upload-envoi-fini');
        _ui.envoiZone.classList.remove('tls-upload-envoi-erreur');
        _ui.texteEnvoi.textContent = 'Envoi de votre photo';
        _ui.envoiZone.style.display = 'block';
    }

    /**
     * Termine l'attente. Avec un message : le bloc devient une
     * confirmation figée (« Photo envoyée ✓ »). Sans message : le bloc
     * disparaît (cas résiduel).
     */
    function terminerAttente(message) {
        if (!_ui) return;
        verrouiller(false);
        if (!message) {
            _ui.envoiZone.style.display = 'none';
            return;
        }
        _ui.envoiZone.classList.remove('tls-upload-envoi-erreur');
        _ui.envoiZone.classList.add('tls-upload-envoi-fini');
        _ui.texteEnvoi.textContent = message;
    }

    /**
     * Termine l'attente sur un ÉCHEC VISIBLE : le bloc reste affiché (en
     * rouge) au lieu de disparaître — sans cela, un échec pouvait passer
     * totalement inaperçu (l'attente disparaissait sans aucune trace).
     */
    function terminerAttenteEchec(message) {
        if (!_ui) return;
        verrouiller(false);
        _ui.envoiZone.classList.remove('tls-upload-envoi-fini');
        _ui.envoiZone.classList.add('tls-upload-envoi-erreur');
        _ui.texteEnvoi.textContent = message || 'Envoi interrompu — réessayez.';
        _ui.envoiZone.style.display = 'block';
    }

    /** Respecte la durée minimale d'affichage de l'animation d'envoi. */
    function attendreFinMinimum() {
        const reste = DUREE_MIN_ATTENTE - (Date.now() - _debutEnvoi);
        if (reste <= 0) return Promise.resolve();
        return new Promise((resolve) => setTimeout(resolve, reste));
    }

    /**
     * Lit l'historique de la session (silencieusement).
     * @returns {Promise<Array|null>} liste des images, ou null si indisponible
     */
    async function lireHistorique() {
        try {
            const reponse = await Toilesia.api.appeler(
                'session/history', 'GET', null, { silencieux: true }
            );
            if (reponse && reponse.succes && Array.isArray(reponse.historique)) {
                return reponse.historique;
            }
        } catch (e) {
            console.warn('[Toilesia.upload] Historique indisponible :', e);
        }
        return null;
    }

    /**
     * Valide puis envoie le fichier au serveur.
     *
     * ⚠️ Ordre voulu : l'ANIMATION D'ATTENTE est affichée AVANT la lecture
     *    locale du fichier, pour un retour visuel immédiat (sur une connexion
     *    lente le client doit voir tout de suite que quelque chose se passe).
     * ⚠️ En cas de réponse en échec, on vérifie d'abord si l'image a malgré
     *    tout été enregistrée (le serveur écrit en base AVANT de répondre :
     *    une réponse corrompue ou perdue ne signifie PAS que l'envoi a
     *    échoué) → aucun faux message d'erreur.
     */
    async function traiterFichier(fichier, onUploaded) {
        if (_envoiEnCours || !_ui) return;

        _ui.erreurZone.style.display = 'none';

        const messageErreur = valider(fichier);
        if (messageErreur) {
            _ui.erreurZone.textContent = messageErreur;
            _ui.erreurZone.style.display = 'block';
            return;
        }

        // 1. Attente AFFICHÉE IMMÉDIATEMENT (verrou + animation) : le client
        //    voit le chargement dès la sélection du fichier, sans attendre la
        //    fin de la lecture locale ni le début de l'envoi réseau.
        _debutEnvoi = Date.now();
        verrouiller(true);
        afficherAttente();

        // 2. Aperçu local (l'image reste estompée tant que l'envoi dure)
        const lecteur = new FileReader();
        lecteur.onload = (e) => {
            _ui.apercuZone.innerHTML = '';
            const label = document.createElement('p');
            label.textContent = 'Votre photo :';
            const img = document.createElement('img');
            img.src = e.target.result;
            img.alt = 'Aperçu de votre photo';
            _ui.apercuZone.appendChild(label);
            _ui.apercuZone.appendChild(img);
            _ui.apercuZone.style.display = 'block';
        };
        lecteur.readAsDataURL(fichier);

        // 3. Identifiants connus AVANT l'envoi : sert à détecter, après un
        //    échec apparent, que l'image a bien été enregistrée.
        const historiqueAvant = await lireHistorique();
        const idsAvant = historiqueAvant
            ? historiqueAvant.map((item) => String(item.image_id))
            : null;

        try {
            const formData = new FormData();
            formData.append('photo', fichier);

            // Silencieux : les notifications (succès ET erreur) sont gérées
            // ici, après vérification — jamais de message d'erreur si l'envoi
            // a en réalité réussi.
            const reponse = await Toilesia.api.appeler(
                'image/upload', 'POST', formData, { silencieux: true }
            );

            if (reponse.succes && reponse.image_id) {
                await finaliserEnvoi(reponse.image_id, reponse.url_apercu || reponse.url, onUploaded);
                return;
            }

            // Réponse en échec : l'image a-t-elle quand même été enregistrée ?
            const recuperee = await retrouverImageAjoutee(idsAvant);
            if (recuperee) {
                await finaliserEnvoi(
                    recuperee.image_id,
                    recuperee.url_apercu || recuperee.url,
                    onUploaded
                );
                return;
            }

            // Échec réel : attente terminée EN ERREUR (bloc visible) + toast.
            const message = Toilesia.api.messageErreur(reponse);
            terminerAttenteEchec(message);
            if (Toilesia.notifier) Toilesia.notifier.erreur(message);
        } catch (e) {
            // Filet de sécurité (session impossible, exception inattendue) :
            // sans cela l'écran resterait verrouillé sur l'attente.
            console.error('[Toilesia.upload] Envoi interrompu :', e);
            const recuperee = await retrouverImageAjoutee(idsAvant);
            if (recuperee) {
                await finaliserEnvoi(
                    recuperee.image_id,
                    recuperee.url_apercu || recuperee.url,
                    onUploaded
                );
                return;
            }
            terminerAttenteEchec();
            if (Toilesia.notifier) {
                Toilesia.notifier.erreur('Envoi impossible. Réessayez dans un instant.');
            }
        }
    }

    /**
     * Clôture un envoi réussi : attend la durée minimale d'animation, fige
     * la confirmation, notifie puis poursuit le parcours (consentement).
     * @param {*} imageId     Identifiant serveur de la photo
     * @param {string} urlApercu URL d'aperçu
     * @param {function} onUploaded Callback du widget
     */
    async function finaliserEnvoi(imageId, urlApercu, onUploaded) {
        _imageId = imageId;
        _urlApercu = urlApercu;
        await attendreFinMinimum();
        terminerAttente('Photo envoyée ✓');
        if (Toilesia.notifier) Toilesia.notifier.succes('Photo envoyée avec succès.');
        onUploaded(_imageId, _urlApercu);
    }

    /**
     * Retrouve dans l'historique une image apparue APRÈS l'envoi (son
     * image_id était inconnu avant). C'est ce qui permet de ne PAS afficher
     * d'erreur quand la réponse a été corrompue ou perdue alors que le
     * serveur avait bien enregistré la photo.
     * @param {Array<string>|null} idsAvant image_id connus avant l'envoi
     * @returns {Promise<object|null>} item d'historique, ou null
     */
    async function retrouverImageAjoutee(idsAvant) {
        if (!idsAvant) return null; // historique indisponible : on ne devine pas

        const historiqueApres = await lireHistorique();
        if (!historiqueApres) return null;

        const anciens = new Set(idsAvant);
        const nouveau = historiqueApres.find(
            (item) => !anciens.has(String(item.image_id))
        );
        return nouveau || null;
    }

    function valider(fichier) {
        if (!TYPES_AUTORISES.includes(fichier.type)) {
            return 'Format non autorisé : utilisez JPG, PNG ou WebP.';
        }
        if (fichier.size > MAX_OCTETS) {
            return 'Image trop lourde (10 Mo maximum).';
        }
        return null;
    }

    Toilesia.upload = {
        afficher: afficher,
        obtenirImageId: () => _imageId,
        obtenirUrlApercu: () => _urlApercu
    };
})();
